import type React from "react";
import { useTranslation } from "react-i18next";
import styles from "./Spinner.module.scss";

const CELLS = ["nw", "n", "ne", "w", "c", "e", "sw", "s", "se"];

export type SpinnerSize = "sm" | "md" | "lg";

export interface SpinnerProps {
	/** sm = 14px (buttons, inline), md = 20px (sections), lg = 32px (pages). */
	size?: SpinnerSize;
	/**
	 * What assistive tech hears. Defaults to the localised "Loading...".
	 * Ignored when `decorative` is set.
	 */
	label?: string;
	/**
	 * Purely visual: no live region, hidden from assistive tech. Use when the
	 * surrounding control already says it is busy (a Button with aria-busy).
	 */
	decorative?: boolean;
	/** Centre the spinner in its container with breathing room (section loads). */
	block?: boolean;
	className?: string;
}

/**
 * The dashboard's loading indicator — loading.dev's "Swirl", in pure CSS. It
 * draws in `currentColor`, so it follows the text colour of its context and
 * works on light islands, dark mode and filled buttons alike.
 *
 * Whole pages should not spin: use `PageSkeleton` so the layout holds its
 * shape. This is for buttons, inline status and small sections.
 */
export const Spinner: React.FC<SpinnerProps> = ({
	size = "md",
	label,
	decorative = false,
	block = false,
	className,
}) => {
	const { t } = useTranslation();
	const classes = [styles.root, block ? styles.block : "", className ?? ""]
		.filter(Boolean)
		.join(" ");

	const swirl = (
		<span
			className={`${styles.swirl} ${styles[size]}`}
			aria-hidden="true"
			data-testid="spinner-swirl"
		>
			{CELLS.map((cell) => (
				<span key={cell} className={styles.cell} />
			))}
		</span>
	);

	if (decorative) {
		return (
			<span className={classes} aria-hidden="true" data-testid="spinner">
				{swirl}
			</span>
		);
	}

	return (
		<output
			className={classes}
			aria-live="polite"
			aria-busy="true"
			data-testid="spinner"
		>
			{swirl}
			<span className={styles.srOnly}>
				{label ?? t("pageLoader.loading", "Loading...")}
			</span>
		</output>
	);
};

export default Spinner;
